import React, { useEffect, useState } from 'react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { TextInput } from '../../../shared/components/TextInput';
import { Textarea } from '../../../shared/components/Textarea';
import type { JobOpening, PublishOpeningPayload, WorkModel } from '../api';
import { useEtapas, useRecrutadores } from '../hooks';
import { validatePublicacao } from '../validation/recrutamentoForms';
import { CidadeUfSelect } from './CidadeUfSelect';
import { WORK_MODEL_LABELS } from './format';
import { RecrutamentoModal } from './RecrutamentoModal';

interface PublicarVagaModalProps {
  open: boolean;
  vaga: JobOpening;
  loading?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: (payload: PublishOpeningPayload) => void;
}

const WORK_MODEL_OPTIONS = (['presencial', 'hibrido', 'remoto'] as WorkModel[]).map((value) => ({
  value,
  label: WORK_MODEL_LABELS[value],
}));

/**
 * Publicação do anúncio.
 *
 * Só campos públicos aparecem — é a mesma fronteira que existe no banco
 * (requisição × vaga) refletida na tela. As etapas obrigatórias do funil
 * vêm desabilitadas para desmarcar: quem tenta receberia 400 do
 * servidor.
 */
export function PublicarVagaModal({
  open,
  vaga,
  loading,
  error,
  onCancel,
  onConfirm,
}: PublicarVagaModalProps) {
  const { data: etapas } = useEtapas();
  const { data: recrutadores } = useRecrutadores();
  const [recruiterId, setRecruiterId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [requirements, setRequirements] = useState('');
  const [city, setCity] = useState('');
  const [workModel, setWorkModel] = useState<WorkModel>('presencial');
  const [deadline, setDeadline] = useState('');
  const [disabled, setDisabled] = useState<string[]>([]);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(vaga.title ?? vaga.requisition?.positionTitle ?? '');
    setDescription(vaga.description ?? '');
    setRequirements(vaga.requirements ?? '');
    setCity(vaga.city ?? '');
    setWorkModel((vaga.workModel as WorkModel) ?? 'presencial');
    setDeadline(vaga.deadline ?? '');
    setDisabled(vaga.disabledStageKeys ?? []);
    setRecruiterId(vaga.recruiterId ?? '');
    setTouched(false);
  }, [open, vaga]);

  const erros = validatePublicacao({ title, description, city });
  const faltando = Object.keys(erros).length > 0;

  return (
    <RecrutamentoModal
      open={open}
      title="Publicar vaga"
      description="O que estiver aqui aparece no portal do candidato. Salário previsto, centro de custo e justificativa não são publicados."
      submitLabel="Publicar"
      size="lg"
      isSubmitting={loading}
      submitError={error}
      canSubmit={!faltando}
      onClose={onCancel}
      onSubmit={() => {
        setTouched(true);
        if (faltando) return;
        onConfirm({
          title: title.trim(),
          description: description.trim(),
          requirements: requirements.trim() || undefined,
          city: city.trim(),
          workModel,
          deadline: deadline || undefined,
          disabledStageKeys: disabled,
          recruiterId: recruiterId || null,
        });
      }}
    >
      <TextInput
        label="Título público"
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        onBlur={() => setTouched(true)}
        error={touched ? erros.title : undefined}
      />
      <Textarea
        label="Descrição"
        rows={4}
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        onBlur={() => setTouched(true)}
        error={touched ? erros.description : undefined}
      />
      <Textarea
        label="Requisitos (opcional)"
        rows={3}
        value={requirements}
        onChange={(event) => setRequirements(event.target.value)}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <CidadeUfSelect
          value={city}
          onChange={(value) => {
            setCity(value);
            setTouched(true);
          }}
          error={touched ? erros.city : undefined}
        />
        <AutocompleteSelect
          label="Modalidade"
          options={WORK_MODEL_OPTIONS}
          value={workModel}
          onChange={(value) => setWorkModel((value || 'presencial') as WorkModel)}
        />
        <TextInput
          label="Data limite (opcional)"
          type="date"
          value={deadline}
          onChange={(event) => setDeadline(event.target.value)}
        />
      </div>

      <AutocompleteSelect
        label="Recrutador responsável (opcional)"
        options={[
          { value: '', label: 'Não definido' },
          ...(recrutadores ?? []).map((recrutador) => ({
            value: recrutador.id,
            label: recrutador.name,
            description: recrutador.email ?? undefined,
          })),
        ]}
        value={recruiterId}
        onChange={setRecruiterId}
      />

      <fieldset>
        <legend className="mb-2 text-sm font-medium text-gray-700">Etapas do processo</legend>
        <div className="space-y-2">
          {(etapas ?? []).map((etapa) => {
            const desativada = disabled.includes(etapa.key);
            const inputId = `etapa-${etapa.key}`;
            return (
              <div key={etapa.key} className="flex items-center gap-2">
                <input
                  id={inputId}
                  type="checkbox"
                  checked={etapa.isRequired || !desativada}
                  disabled={etapa.isRequired}
                  onChange={(event) =>
                    setDisabled((previous) =>
                      event.target.checked
                        ? previous.filter((key) => key !== etapa.key)
                        : [...previous, etapa.key],
                    )
                  }
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-60"
                />
                <label
                  htmlFor={inputId}
                  className={`text-sm ${etapa.isRequired ? 'text-gray-400' : 'text-gray-700'}`}
                >
                  {etapa.label}
                  {etapa.isRequired && ' (obrigatória)'}
                </label>
              </div>
            );
          })}
        </div>
      </fieldset>
    </RecrutamentoModal>
  );
}
